import type { BucketRootFolder, EncoderWatchfolder } from './keldaConfigPrimitives';
import type { KeldaMediaPipelineInputDto } from './keldaMediaPipelineDtos';

// The idea behind Kelda node status design is that we will have a higher update rate, than the polling rate of the clients.
// This essentially means that we can in the API layer determine which nodes are no longer there, by checking that a certain
// amount of time has passed, and can therefore be marked "stale". The API layer can remove these "stale" statuses implicitly,
// and the client would only ever receive statuses of nodes that are still "alive".
// kelda_status is a separate status entity that can represent the general status/wellbeing of a whole kelda setup, while kelda_node_status
// represents worker status of a single worker in a kelda setup. Grouping them in this way, we can query for both statuses with 1 query.
// 'kelda_worker_status' is added as preparation for possibly having individual worker statuses, and we can use this value to identify them as a

// The idea behind Kelda node status design is that we will have a higher update rate, than the polling rate of the clients.
// This essentially means that we can in the API layer determine which nodes are no longer there, by checking that a certain
// amount of time has passed, and can therefore be marked "stale". The API layer can remove these "stale" statuses implicitly,
// and the client would only ever receive statuses of nodes that are still "alive".
// kelda_status is a separate status entity that can represent the general status/wellbeing of a whole kelda setup, while kelda_node_status
// represents worker status of a single worker in a kelda setup. Grouping them in this way, we can query for both statuses with 1 query.
// 'kelda_worker_status' is added as preparation for possibly having individual worker statuses, and we can use this value to identify them as a
import type { SourceListKey } from './liveSourceDtos';
import type { NdiSourceDto } from './ndiDtos';
import type { KeldaInternalPlayoutDeviceId } from './playoutDtos';

// different entity type.
export const statusEntityTypes = ['kelda_status', 'kelda_worker_status'] as const;
export type StatusType = typeof statusEntityTypes[number];

// Making the status of a given node machine readable / queryable, but also so clients can map this to a certain look such as color/icon
export const statusTypes = ['unknown', 'ok', 'error', 'stale'] as const; // expand as needed.
export type Status = typeof statusTypes[number];

// The threshold that should be used by client & lambda to identify nodes no longer responding
export const keldaStatusMaxAgeMs = 30000;

// The update rate of Kelda's providing a heart beat.
// This value should be lower than the keldaStatusMaxAgeMs by a margin such that
// we rarely would go beyond the threshold time without receiving a new status
export const updateRateMs = keldaStatusMaxAgeMs / 2;

export const clientPollingRateMs = keldaStatusMaxAgeMs / 3;

export interface EncoderWatchfolderStatus extends EncoderWatchfolder {
  /**
   * true if this AME folder is being used by Kelda.
   */
  inUse: boolean;

  /**
   * If this folder is in use, this property will be undefined
   * when everything works fine. Otherwise, it will contain
   * a message describing what is wrong.
   */
  errorMessage?: string;
}

export interface EncoderWatchfolderStatusResponseDto extends EncoderWatchfolderStatus {
  // The presence of this field means the given item was
  // recently reported as being rendered using this AME watch folder.
  conformActivity?: {
    itemId: string
  }
}

export interface StorageFolderStatus {
  name: string;
  errorMessage?: string;
}

export const isStorageFolderStatus = (obj: unknown): obj is StorageFolderStatus[] => {
  return Array.isArray(obj) && obj.every(status => typeof status.name === 'string');
};


export interface KeldaStatusDto {
  // NOTE: This ID is not our usual UUID, but rather the hostname
  // of the container; the same as the "name" property.
  // And so it can not be assumed to be unique across all statuses.
  id: string;
  // The hostname of the container running the kelda instance,
  name: string;

  modifiedOn: string;
  status: Status;
  configId: string;
  statusMessage?: string;
  buckets: BucketRootFolder[];
  ameWatchfolders: EncoderWatchfolderStatusResponseDto[];
  dvrWatchfolders: EncoderWatchfolderStatusResponseDto[];
  storageFolders: StorageFolderStatus[];
  playoutDevices: KeldaInternalPlayoutDeviceId[];
  ndiSources: NdiSourceDto[];
  liveSourceStatuses: LiveSourceStatusUpdate[];
  keldaVersion?: string;
  instanceId?: string;
  hasLegacyFilesInPrimaryRoot?: boolean;
  mediaPipelineInputs?: KeldaMediaPipelineInputDto[];
}

export type LiveSourceStatusUpdate = LiveSourceWithOkStatusUpdate | LiveSourceWithErrorUpdate;

export type LiveSourceWithOkStatusUpdate = {
  liveSourceId: string,
  status: 'ok',
  pipelines: PipelineStatusUpdate[]
};

export type LiveSourceWithErrorUpdate = {
  liveSourceId: string,
  status: 'error',
  errorMsg: string,
};

export type PipelineStatusUpdate = PipelineWithOkStatusUpdate | PipelineWithErrorUpdate | PipelineWithOccupiedStatusUpdate;

export type PipelineWithOkStatusUpdate = {
  pipelineId: string,
  status: 'ok',

  // Defined if the status update is also used for detecting/reporting
  // the name of the pipeline.
  // Used for the Elemental proxy, but not for VDCP.
  pipelineName: string | undefined,
};

export type PipelineWithOccupiedStatusUpdate = {
  pipelineId: string,
  status: 'occupied',
  liveSource: { id: string, name: string },
};

export type PipelineWithErrorUpdate = {
  pipelineId: string,
  status: 'error',
  errorMsg: string
};


export interface UpdateOrCreateKeldaStatusDto {
  /**
   * The hostname used by the given Kelda instance reporting this status.
   */
  name: string;

  status: Status;
  statusMessage?: string;
  buckets?: BucketRootFolder[];
  ameWatchfolders?: EncoderWatchfolderStatus[];
  dvrWatchfolders?: EncoderWatchfolderStatus[];
  storageFolders?: StorageFolderStatus[];
  playoutDevices?: KeldaInternalPlayoutDeviceId[];
  ndiSources?: NdiSourceDto[];
  liveSourceStatuses?: LiveSourceStatusUpdate[];
  mediaPipelineInputs?: KeldaMediaPipelineInputDto[];
  mediaPipelineSources?: Record<SourceListKey, string[]>;

  /**
   * A string like "2.2.0"
   */
  keldaVersion: string;

  /**
   * The unique ID used by the given Kelda instance reporting this status.
   * Combined with the hostname, this information are useful when we want to connect
   * to a specific Kelda instance ith mtunnel.
   */
  instanceId: string;

  /**
   * Are there files named after item IDs as direct children of the
   * primary root folder ?
   */
  hasLegacyFilesInPrimaryRoot: boolean;
}

export const isCreateKeldaStatusDto = (dto: unknown) => {
  if (!isObject(dto)) return false;
  return 'name' in dto && 'status' in dto;
};

function isObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === 'object';
}
